import { lazy, Suspense } from 'react';
import { Clock, Mail, MapPin, Phone } from 'lucide-react';
import { t } from '@/lib/site/i18n';
import { registerWidget, type WidgetContext, type WidgetProps } from './registry';
import { AppearanceWrap, siteBlockText, type BlockAppearance } from './blocks';

// ============================================================================
// lib/widgets/blocks-contacts.tsx — блоки КОНТАКТОВ (ТЗ §5 «хвосты»):
// `contacts` (адрес/телефон/email/часы + карта-заглушка БЕЗ внешних запросов)
// и `map` (Leaflet ЛЕНИВО; координаты — числа ИЛИ {field:"колонка"} записи
// item-страницы; нет координат → блок не рендерится — детерминированно).
// Регистрируется из registerSiteMarketplaceBlocks (реестр блоков сайта).
// ============================================================================

const LazySiteMap = lazy(() => import('./SiteMapLeaflet'));

/** resolveCoord — координата блока: число/строка, биндинг {field} из record. */
function resolveCoord(value: unknown, ctx: WidgetContext): number {
  let raw = '';
  if (value && typeof value === 'object' && typeof (value as { field?: unknown }).field === 'string') {
    raw = String(ctx.record?.[(value as { field: string }).field] || '');
  } else {
    raw = String(value ?? '');
  }
  const n = parseFloat(raw.trim().replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
}

function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

// ----------------------------- contacts -----------------------------

interface ContactExtra { label?: string; value?: string }

function parseExtras(raw: unknown, ctx: WidgetContext): ContactExtra[] {
  const text = t(raw as never, ctx.siteLocale || '', ctx.siteDefaultLocale || 'ru', ctx.record);
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e) => e && typeof e === 'object') as ContactExtra[];
  } catch {
    return [];
  }
}

function ContactsBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const title = siteBlockText(props, ctx, 'title');
  const address = siteBlockText(props, ctx, 'address');
  const phone = siteBlockText(props, ctx, 'phone');
  const email = siteBlockText(props, ctx, 'email');
  const hours = siteBlockText(props, ctx, 'hours');
  const extras = parseExtras(props.extra, ctx);
  const showMapStub = props.mapStub !== false;

  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      {title && <div className="text-xl font-semibold text-[var(--site-text)] mb-2">{title}</div>}
      <div className="flex flex-col gap-2 text-sm text-[var(--site-text)]">
        {address && (
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0 opacity-70" />
            <span>{address}</span>
          </div>
        )}
        {phone && (
          <a href={telHref(phone)} className="flex items-center gap-2 hover:opacity-70">
            <Phone className="h-4 w-4 shrink-0 opacity-70" />
            <span>{phone}</span>
          </a>
        )}
        {email && (
          <a href={`mailto:${email}`} className="flex items-center gap-2 hover:opacity-70">
            <Mail className="h-4 w-4 shrink-0 opacity-70" />
            <span>{email}</span>
          </a>
        )}
        {hours && (
          <div className="flex items-start gap-2">
            <Clock className="h-4 w-4 mt-0.5 shrink-0 opacity-70" />
            <span className="whitespace-pre-line">{hours}</span>
          </div>
        )}
        {extras.map((e, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="opacity-70">{e.label}</span>
            {e.value && (isExternalUrl(e.value)
              ? <a href={e.value} target="_blank" rel="noopener" className="hover:opacity-70">{e.value}</a>
              : <span>{e.value}</span>)}
          </div>
        ))}
      </div>
      {/* Карта-заглушка: БЕЗ внешних запросов (реальная карта — блок «Карта»). */}
      {showMapStub && address && (
        <div className="mt-3 flex h-32 items-center justify-center rounded-lg border border-dashed border-[var(--site-border)] text-xs opacity-60">
          <MapPin className="h-4 w-4 mr-1" /> {address}
        </div>
      )}
    </AppearanceWrap>
  );
}

// ----------------------------- map -----------------------------

function MapBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const lat = resolveCoord(props.lat, ctx);
  const lon = resolveCoord(props.lon, ctx);
  // Нет координат (в т.ч. пустой {field} записи) → блок не рендерится.
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const zoom = Math.max(1, Math.min(19, Number(props.zoom) || 15));
  const height = Math.max(120, Number(props.height) || 260);
  const title = siteBlockText(props, ctx, 'title');
  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      <Suspense fallback={<div style={{ height }} className="flex items-center justify-center rounded-lg border border-[var(--site-border)] text-xs opacity-60">Загрузка карты…</div>}>
        <LazySiteMap lat={lat} lon={lon} zoom={zoom} height={height} title={title} />
      </Suspense>
    </AppearanceWrap>
  );
}

/** registerSiteContactBlocks — блоки контактов/карты (вызывается из
 *  registerSiteMarketplaceBlocks — единая точка регистрации блоков сайта). */
export function registerSiteContactBlocks(): void {
  registerWidget({
    type: 'contacts',
    display: 'Контакты',
    description: 'Адрес/телефон/email/часы + карта-заглушка (без внешних запросов)',
    siteOk: true,
    appearanceSchema: true,
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text' },
      { key: 'address', label: 'Адрес', type: 'text', help: 'можно {column_name} записи или {field:…} на странице объекта' },
      { key: 'phone', label: 'Телефон', type: 'text' },
      { key: 'email', label: 'Электронная почта', type: 'text' },
      { key: 'hours', label: 'Часы работы', type: 'textarea' },
      { key: 'extra', label: 'Дополнительно (JSON [{label,value}])', type: 'textarea' },
      { key: 'mapStub', label: 'Карта-заглушка (адрес)', type: 'boolean' },
    ],
    render: (props, ctx) => <ContactsBlock props={props} ctx={ctx} />,
  });
  registerWidget({
    type: 'map',
    display: 'Карта',
    description: 'Leaflet-карта (ленивая загрузка); координаты — числа или {field} записи',
    siteOk: true,
    appearanceSchema: true,
    propsSchema: [
      { key: 'lat', label: 'Широта', type: 'text', placeholder: '55.7558', help: 'число или {field:"geo_lat"} со страницы объекта' },
      { key: 'lon', label: 'Долгота', type: 'text', placeholder: '37.6173', help: 'число или {field:"geo_lon"} со страницы объекта' },
      { key: 'zoom', label: 'Масштаб (1–19)', type: 'number', placeholder: '15' },
      { key: 'height', label: 'Высота, px', type: 'number', placeholder: '260' },
      { key: 'title', label: 'Подпись маркера', type: 'text' },
    ],
    render: (props, ctx) => <MapBlock props={props} ctx={ctx} />,
  });
}
